import { motion } from "framer-motion";
import { Mail, Github, Phone, MapPin, ArrowUpRight, Download } from "lucide-react";
import { SectionLabel } from "./Section";
import { LINKS, RESUME_PDF } from "../data/content";

const Word = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      initial={{ y: "60%", opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Contact = ({ t }) => {
  const items = [
    { icon: Mail, label: "Email", value: LINKS.email, href: `mailto:${LINKS.email}`, testid: "contact-email" },
    { icon: Github, label: "GitHub", value: LINKS.githubLabel, href: LINKS.github, testid: "contact-github" },
    { icon: Phone, label: "WhatsApp", value: LINKS.phonePrimary, href: "https://api.whatsapp.com/send/?phone=5511947165462&text=Ol%C3%A1", testid: "contact-phone" },
  ];

  return (
    <section id="contact" className="relative pt-28 md:pt-40 pb-12 px-6 md:px-10 overflow-hidden">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60rem] h-[30rem] bg-[#2563eb]/15 blur-[150px] rounded-full -z-0 pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <SectionLabel num={t.contact.num} label={t.contact.label} id="contact" />

        <h2 className="font-display font-extrabold tracking-tighter leading-[0.85] text-[clamp(3.5rem,15vw,13rem)]">
          <Word delay={0}>{t.contact.big1}</Word>
          <Word delay={0.1}>
            <span className="text-stroke">{t.contact.big2}</span>
          </Word>
        </h2>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <p className="text-[#8b95a5] text-lg leading-relaxed max-w-md">{t.contact.sub}</p>
            <div className="mt-6 flex items-center gap-2 text-[#8b95a5]">
              <MapPin className="w-4 h-4 text-[#2563eb]" />
              <span className="font-mono text-sm">{LINKS.location}</span>
            </div>
            <a
              data-testid="contact-download"
              href={RESUME_PDF}
              download
              className="group inline-flex items-center gap-2 mt-8 px-6 py-3.5 rounded-full bg-[#2563eb] text-white font-medium hover:bg-[#1d4ed8] transition-colors duration-300 accent-glow"
            >
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
              {t.contact.download}
            </a>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <div className="border-t border-[#1a1d27]">
              {items.map((it) => (
                <a
                  key={it.label}
                  href={it.href}
                  target={it.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={it.testid}
                  className="group flex items-center justify-between gap-4 py-6 border-b border-[#1a1d27] hover:px-2 transition-[padding] duration-300"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <it.icon className="w-5 h-5 text-[#8b95a5] group-hover:text-[#2563eb] transition-colors duration-300 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#8b95a5]">{it.label}</div>
                      <div className="font-display font-semibold text-lg md:text-xl text-white truncate group-hover:text-[#2563eb] transition-colors duration-300">
                        {it.value}
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#8b95a5] group-hover:text-[#2563eb] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 pt-6 border-t border-[#1a1d27] flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-display font-extrabold text-lg tracking-tight">
            Fagner Nunes<span className="text-[#2563eb]">.</span>
          </span>
          <span className="font-mono text-[11px] text-[#8b95a5] tracking-[0.15em] uppercase">
            © {new Date().getFullYear()} · {t.contact.rights}
          </span>
        </div>
      </div>
    </section>
  );
};
